/**
 * Accessible Tabs
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role}
 * @param el Any element that contains `'[role="tab"]'` and `'[role="tablist"]'` elements
 * @example
 *   import { initA11YTabs } from './path-to/tabs'
 *   initA11YTabs(document.querySelector('.foo'))
 */
export const initA11YTabs = (el: HTMLElement) => {
  /** Tabs */
  const tabs: HTMLButtonElement[] = Array.from(
    el.querySelectorAll('[role="tab"]')
  );

  /** Tab list */
  const tabList: HTMLDivElement = el.querySelector('[role="tablist"]');

  if (!tabs || !tabs.length || !tabList) return;

  /** The index of the element in {@link tabs} that is currently focused */
  let focusIndex = 0;

  /**
   * On keydown
   * @param e Keyboard event
   */
  const onKeydown = (e: KeyboardEvent) => {
    // Move right
    if (e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
      tabs[focusIndex].tabIndex = -1;

      if (e.code === 'ArrowRight') {
        // Move right
        focusIndex++;

        // If we're at the end, go to the start
        if (focusIndex >= tabs.length) {
          focusIndex = 0;
        }
      } else if (e.code === 'ArrowLeft') {
        // Move left
        focusIndex--;

        // If we're at the start, move to the end
        if (focusIndex < 0) {
          focusIndex = tabs.length - 1;
        }
      }

      tabs[focusIndex].tabIndex = 0;
      tabs[focusIndex].focus();
    }
  };

  tabList.addEventListener('keydown', onKeydown);

  /**
   * Change tabs
   * @param e Click event
   */
  const changeTabs = (e: MouseEvent) => {
    /** Current tab */
    const target = <HTMLElement>e.target;
    const parent = target.parentNode;
    const grandparent = parent.parentNode;

    // Remove all current selected tabs
    parent
      .querySelectorAll('[aria-selected="true"]')
      .forEach((x) => x.setAttribute('aria-selected', 'false'));

    // Set this tab as selected
    target.setAttribute('aria-selected', 'true');

    // Hide all tab panels
    const panels: HTMLDivElement[] = Array.from(
      grandparent.querySelectorAll('[role="tabpanel"]')
    );

    panels.forEach((x) => {
      x.hidden = true;
    });

    // Show the selected panel
    grandparent.parentNode
      .querySelector(`#${target.getAttribute('aria-controls')}`)
      .removeAttribute('hidden');
  };

  /** Add a click event handlers to {@link tabs} */
  tabs.forEach((x) => x.addEventListener('click', changeTabs));
};
